import { ac, orgRoles } from "@doculegis/shared/auth/permissions";
import type { AppId, EnabledOrgRoles, OrgRolePermissions } from "@doculegis/shared";
import { createAuthClient } from "better-auth/react";
import { jwtClient, organizationClient } from "better-auth/client/plugins";
import { AUTH_BASE, authUrl } from "@/lib/api-config";

/** Auth service URL — same-origin /api/auth proxy in dev; direct URL in production. */
const baseURL = AUTH_BASE;

export const authClient = createAuthClient({
  baseURL,
  fetchOptions: {
    credentials: "include",
  },
  plugins: [jwtClient(), organizationClient({ ac, roles: orgRoles })],
});

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  fullName?: string | null;
  termsAcceptedAt?: Date | string | null;
  termsDocumentVersion?: string | null;
};

export type ActiveOrganization = {
  id: string;
  name: string;
  slug: string;
};

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string, leewaySeconds = 30): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") {
    return false;
  }
  return payload.exp <= Math.floor(Date.now() / 1000) + leewaySeconds;
}

function isJwtFormat(token: string): boolean {
  return token.split(".").length === 3;
}

let cachedToken: string | null = null;
let inflightPromise: Promise<string | null> | null = null;
let cacheEpoch = 0;

/** Clear the in-memory JWT cache (call on sign-out / forced refresh). */
export function clearJwtTokenCache(): void {
  cachedToken = null;
  cacheEpoch += 1;
  inflightPromise = null;
}

async function fetchJwtTokenFromAuth(): Promise<string | null> {
  const { data, error } = await authClient.token();
  if (error || !data?.token || !isJwtFormat(data.token)) return null;

  const token = data.token;
  if (!isJwtExpired(token)) {
    return token;
  }

  const retry = await authClient.token();
  if (
    retry.error ||
    !retry.data?.token ||
    !isJwtFormat(retry.data.token) ||
    isJwtExpired(retry.data.token)
  ) {
    return null;
  }

  return retry.data.token;
}

/**
 * Return a JWT for API calls (auth service session cookie required).
 * Caches a valid token and coalesces parallel callers onto one in-flight fetch.
 */
export async function getJwtToken(): Promise<string | null> {
  if (cachedToken && !isJwtExpired(cachedToken)) {
    return cachedToken;
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  const epoch = cacheEpoch;
  let promise!: Promise<string | null>;
  promise = (async () => {
    try {
      const token = await fetchJwtTokenFromAuth();
      if (epoch === cacheEpoch) {
        cachedToken = token;
      }
      return token;
    } finally {
      if (inflightPromise === promise) {
        inflightPromise = null;
      }
    }
  })();

  inflightPromise = promise;
  return promise;
}

/** Re-sync session cookie state, then fetch a fresh JWT. */
export async function refreshKmJwtToken(): Promise<string | null> {
  clearJwtTokenCache();
  await authClient.getSession();
  return getJwtToken();
}

export async function createOrgMember(body: {
  email: string;
  password: string;
  name?: string;
  role: string;
}) {
  const response = await fetch(authUrl("/api/org/members"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to create user");
  }
  return data;
}

export interface OrgMemberRecord {
  id: string;
  userId: string;
  role: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    fullName?: string | null;
    emailVerified?: boolean;
    banned?: boolean | null;
    role?: string | null;
    createdAt?: string | Date | null;
  };
}

export async function listOrgMembers(): Promise<{
  members: OrgMemberRecord[];
  memberLimit: number;
  memberCount: number;
  enabledOrgRoles: EnabledOrgRoles;
}> {
  const response = await fetch(authUrl("/api/org/members"), {
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to load members");
  }
  return data as {
    members: OrgMemberRecord[];
    memberLimit: number;
    memberCount: number;
    enabledOrgRoles: EnabledOrgRoles;
  };
}

export interface OrgMemberDirectoryEntry {
  userId: string;
  email: string;
  name: string;
  orgRole: string;
}

export async function listOrgMemberDirectory(): Promise<{
  members: OrgMemberDirectoryEntry[];
}> {
  const response = await fetch(authUrl("/api/org/members/directory"), {
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to load organization members");
  }
  return data as { members: OrgMemberDirectoryEntry[] };
}

export async function setOrgMemberPassword(userId: string, newPassword: string): Promise<void> {
  const response = await fetch(authUrl(`/api/org/members/${userId}/password`), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newPassword }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to set password");
  }
}

export async function resendOrgMemberVerification(userId: string): Promise<void> {
  const response = await fetch(authUrl(`/api/org/members/${userId}/resend-verification`), {
    method: "POST",
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to resend verification email");
  }
}

export interface OrgSettings {
  organizationName: string;
  enabledOrgRoles: EnabledOrgRoles;
  enabledApps?: AppId[];
  orgRolePermissions: OrgRolePermissions;
  resolvedRolePermissions?: OrgRolePermissions;
  memberCount: number;
  memberLimit: number;
}

export interface OrgPermissions {
  enabledOrgRoles: EnabledOrgRoles;
  enabledApps?: AppId[];
  orgRolePermissions: OrgRolePermissions;
  resolvedRolePermissions: OrgRolePermissions;
}

export async function getOrgPermissions(): Promise<OrgPermissions> {
  const response = await fetch(authUrl("/api/org/permissions"), {
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to load organization permissions");
  }
  return data as OrgPermissions;
}

export async function getOrgSettings(): Promise<OrgSettings> {
  const response = await fetch(authUrl("/api/org/settings"), {
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to load organization settings");
  }
  return data as OrgSettings;
}

export async function updateOrgSettings(body: {
  orgRolePermissions: OrgRolePermissions;
}): Promise<OrgSettings> {
  const response = await fetch(authUrl("/api/org/settings"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? "Failed to save organization settings");
  }
  return data as OrgSettings;
}
